import { readViteEnv } from '../readViteEnv';
import {
  DEFAULT_ADMIN_OIDC_CLIENT_ID,
  DEFAULT_ADMIN_OIDC_POST_LOGOUT_PATH,
  DEFAULT_ADMIN_OIDC_REDIRECT_PATH,
  parseAdminAuthMode,
  resolveAuthServerBase,
  type AdminAuthMode,
} from './oidcCore';

/**
 * RBAC Phase 3 — admin SPA auth mode + OIDC client settings (build-time `VITE_*` or runtime
 * `RUNTIME_VITE_*` via env-config.js).
 *
 * - `VITE_ADMIN_AUTH` = `password` (default; orchestrator operator login) | `oidc`
 * - `VITE_AUTH_SERVER_BASE` — browser base for auth-server API + OIDC (e.g. `/auth-server`)
 * - `VITE_AUTH_SERVER_ISSUER` — optional exact `iss`; otherwise read from discovery
 * - `VITE_ADMIN_OIDC_CLIENT_ID` (default `admin-console`)
 * - `VITE_ADMIN_OIDC_REDIRECT_PATH` (default `/auth/callback`)
 * - `VITE_ADMIN_OIDC_POST_LOGOUT_PATH` (default `/login`)
 */
export function adminAuthMode(): AdminAuthMode {
  return parseAdminAuthMode(readViteEnv('VITE_ADMIN_AUTH'));
}

export function isOidcAdminAuth(): boolean {
  return adminAuthMode() === 'oidc';
}

export type AdminOidcConfig = {
  authServerBase: string;
  issuer: string | null;
  clientId: string;
  redirectPath: string;
  postLogoutPath: string;
};

function pathOr(raw: string | undefined, fallback: string): string {
  const t = (raw ?? '').trim();
  if (!t) return fallback;
  return t.startsWith('/') ? t : `/${t}`;
}

export function adminOidcConfig(): AdminOidcConfig {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  return {
    authServerBase: resolveAuthServerBase(readViteEnv('VITE_AUTH_SERVER_BASE'), origin),
    issuer: readViteEnv('VITE_AUTH_SERVER_ISSUER') ?? null,
    clientId: readViteEnv('VITE_ADMIN_OIDC_CLIENT_ID') ?? DEFAULT_ADMIN_OIDC_CLIENT_ID,
    redirectPath: pathOr(readViteEnv('VITE_ADMIN_OIDC_REDIRECT_PATH'), DEFAULT_ADMIN_OIDC_REDIRECT_PATH),
    postLogoutPath: pathOr(readViteEnv('VITE_ADMIN_OIDC_POST_LOGOUT_PATH'), DEFAULT_ADMIN_OIDC_POST_LOGOUT_PATH),
  };
}
